import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { legalDocs, type LegalDocId } from '../../data/legal';
interface LegalModalProps {
  openId: LegalDocId | null;
  onClose: () => void;
  onSwitch: (id: LegalDocId) => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function LegalModal({
  openId,
  onClose,
  onSwitch
}: LegalModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const doc = legalDocs.find(d => d.id === openId);
  useEffect(() => {
    if (!openId) return;
    const previous = document.activeElement as HTMLElement | null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [openId, onClose]);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: 0
    });
  }, [openId]);
  return <AnimatePresence data-id="element-69" data-mp="3ecilsnogbh8u">
      {doc && <motion.div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-70" data-mp="3edjvanptjvog">
          <button type="button" aria-label="Cerrar" tabIndex={-1} onClick={onClose} className="absolute inset-0 cursor-default bg-ink/85 backdrop-blur-sm" data-id="element-71" data-mp="ft1xt3dxidky" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="legal-title" initial={{
        opacity: 0,
        y: 16,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease
      }} className="relative flex max-h-[88svh] w-full max-w-2xl flex-col overflow-hidden rounded-t-xl border border-line bg-surface sm:rounded-xl" data-id="element-72" data-mp="61nuslmrqvn2">
            <div className="border-b border-line px-6 pb-0 pt-6 sm:px-8" data-id="element-73" data-mp="zhg9wpb1xchj">
              <div className="flex items-start justify-between gap-6" data-id="element-74" data-mp="ug90ymx6a79a">
                <div data-id="element-75" data-mp="pih7hogvqd2z">
                  <h2 id="legal-title" className="font-display text-3xl font-black uppercase leading-none text-white" data-id="element-76" data-mp="piqownnuzbx2">
                    {doc.title}
                  </h2>
                  <p className="mt-2 text-sm text-muted" data-id="element-77" data-mp="fsnwopoka1zs">{doc.updated}</p>
                </div>
                <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line text-white transition-colors duration-150 ease-out hover:border-brand hover:bg-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand" data-id="element-78" data-mp="br2ecot9cvi7">
                  <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-79" data-mp="2ig4g9mbro31p" />
                </button>
              </div>
              <nav className="-mb-px mt-5 flex gap-5 overflow-x-auto" aria-label="Documentos legales" data-id="element-80" data-mp="2reziknbowszq">
                {legalDocs.map(d => <button key={d.id} type="button" onClick={() => onSwitch(d.id)} aria-current={d.id === doc.id ? 'page' : undefined} className={`whitespace-nowrap border-b-2 pb-3 text-sm font-semibold transition-colors duration-150 ease-out ${d.id === doc.id ? 'border-brand text-white' : 'border-transparent text-muted hover:text-white'}`} data-id="element-81" data-mp="1uq0ab8b3vr7j">
                    {d.label}
                  </button>)}
              </nav>
            </div>

            <div ref={scrollRef} className="overflow-y-auto px-6 py-6 sm:px-8" data-id="element-82" data-mp="wvh7gd8kjubz">
              <div className="space-y-6" data-id="element-83" data-mp="rxfnlq3bjqzg">
                {doc.sections.map((s, i) => <section key={s.heading} data-id="element-84" data-mp="16m2d2kmntz4e" data-mpi={i}>
                    <h3 className="flex items-baseline gap-3 font-display text-xl font-bold uppercase text-white" data-id="element-85" data-mp="16mtzp5ya4mij">
                      <span className="text-brand" data-id="element-86" data-mp="1flp8mpspkapj">{i + 1}.</span>
                      {s.heading}
                    </h3>
                    <p className="mt-2 leading-relaxed text-muted" data-id="element-87" data-mp="1andw27b13m3m">{s.body}</p>
                  </section>)}
              </div>
            </div>

            <div className="border-t border-line px-6 py-4 sm:px-8" data-id="element-88" data-mp="1phipumhdgsdn">
              <button type="button" onClick={onClose} className="w-full rounded-md bg-brand px-6 py-3 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors duration-150 ease-out hover:bg-neon sm:w-auto" data-id="element-89" data-mp="1klbhyo5f16t2">
                Entendido
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}